import { UpcomingEvent } from "@/lib/api/upcoming.ts";
import { Bell, Calendar, ChevronRight, X } from "lucide-react";
import { useFormatEventDetails } from "@/lib/hooks/use-format-event-details.ts";
import { getMenteeDisplayName } from "@/lib/redacted-display";
import { Fragment, useState } from "react";
import { SessionEditMode } from "@/components/session/session-edit-mode.tsx";
import { NoteCard } from "@/components/conversation-details/notes/note-card.tsx";
import { useEditNote } from "@/lib/api/notes.ts";
import { useNoteUtils } from "@/lib/hooks/conversations/use-note-utils.ts";
import { useQueryClient } from "@tanstack/react-query";

interface UpcomingEventCardProps {
  upcomingEvent: UpcomingEvent;
}

export function UpcomingEventCard({ upcomingEvent }: UpcomingEventCardProps) {
  const [editMode, setEditMode] = useState(false);
  const { day, date, month, startsIn, time } = useFormatEventDetails(
    upcomingEvent.date,
  );

  const queryClient = useQueryClient();
  const { mutateAsync: updateNote, isPending } = useEditNote();
  const { convertFormValuesToReminder } = useNoteUtils();

  return (
    <div
      className="inline-flex items-center justify-between rounded-xl border border-[#e2e2e2] bg-white px-6 py-3"
      onClick={() => {
        if (editMode) return;
        setEditMode(!editMode);
      }}
    >
      {editMode ? (
        <Fragment>
          {upcomingEvent.type === "session" ? (
            <SessionEditMode
              closeForm={() => setEditMode(false)}
              session={upcomingEvent.session!}
              className="w-full"
              handleSessionUpdated={() => {
                queryClient.invalidateQueries({
                  predicate: (query) => {
                    return query.queryKey[0] === "upcoming-events";
                  },
                });
              }}
            />
          ) : (
            <div className="w-full">
              <div className="flex flex-row items-center justify-between">
                <p className="mb-2 text-base font-bold"></p>

                <button onClick={() => setEditMode(false)}>
                  <X size={24} className="text-gray-600" />
                </button>
              </div>

              <NoteCard
                noWrapper
                noteActions={{
                  handleSave: async (payload) => {
                    const reminder = convertFormValuesToReminder(
                      payload.date,
                      payload.time,
                      payload.timezone,
                    );

                    // We don't have to wait for the update to finish
                    await updateNote({
                      id: upcomingEvent.note!.id,
                      title: payload.title,
                      content: payload.content,
                      mentorshipId: upcomingEvent.note!.mentorshipId,
                      reminder: reminder || null,
                      metadata: {
                        ...(upcomingEvent.note!.metadata || {}),
                        reminderTimezone: payload.timezone,
                      },
                    });

                    queryClient.invalidateQueries({
                      predicate: (query) => {
                        return query.queryKey[0] === "upcoming-events";
                      },
                    });
                    setEditMode(false);
                  },
                }}
                isSubmitting={isPending}
                cardActions={{
                  onCancel: () => setEditMode(false),
                }}
                editMode={editMode}
                note={upcomingEvent.note!}
              />
            </div>
          )}
        </Fragment>
      ) : (
        <Fragment>
          <div className="flex shrink grow basis-0 items-center justify-start gap-3 self-stretch">
            <div className="relative h-6 w-6">
              {upcomingEvent.type === "session" ? <Calendar /> : <Bell />}
            </div>
            <div className="inline-flex w-9 flex-col items-center justify-start">
              <div className="text-center text-xs font-bold leading-3 text-black">
                {day}
              </div>
              <div className="fon t-bold text-center text-2xl leading-7 text-black">
                {date}
              </div>
              <div className="text-center text-xs font-normal uppercase leading-none text-black">
                {month}
              </div>
            </div>
            <div className="flex items-start justify-start self-stretch">
              <div className="relative h-14 w-px rounded-full bg-[#e2e2e2]" />
            </div>
            {upcomingEvent.type === "session" ? (
              <div className="inline-flex shrink grow basis-0 flex-col items-start justify-center gap-1">
                <div className="text-center text-base font-normal leading-snug text-black">
                  Session with{" "}
                  {getMenteeDisplayName(upcomingEvent.session?.mentee)}
                </div>
                <div className="inline-flex items-center justify-start gap-1">
                  <div className="text-center text-xs font-normal leading-none text-[#5e5e5e]">
                    {startsIn}
                  </div>
                  <div className="text-center text-xs font-normal leading-none text-[#5e5e5e]">
                    {time}
                  </div>
                </div>
              </div>
            ) : (
              <div className="inline-flex shrink grow basis-0 flex-col items-start justify-start gap-1.5">
                <div className="self-stretch text-base font-bold leading-tight text-black">
                  {upcomingEvent.title}
                </div>
                <div className="inline-flex items-center justify-start gap-1 self-stretch">
                  <div className="text-sm font-normal leading-tight text-[#5e5e5e]">
                    {upcomingEvent.content}. Remind me on
                  </div>
                  <div className="flex items-center justify-center gap-2 rounded bg-[#ffede6] px-2 py-1">
                    <div className="text-center text-xs font-normal leading-tight text-[#7a3000]">
                      {time}
                    </div>
                  </div>
                  <div className="text-sm font-normal leading-tight text-[#5e5e5e]">
                    to check in
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="relative h-6 w-6">
            <ChevronRight />
          </div>
        </Fragment>
      )}
    </div>
  );
}
